import { ContentCopy, OpenInNew } from '@mui/icons-material';
import {
  Autocomplete,
  AutocompleteProps,
  Avatar,
  Box,
  CircularProgress,
  ListItem,
  Popover,
  Skeleton,
  Tooltip,
  styled,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACTIONS } from 'src/auth/utils/defineAbilityFor';
import { Can } from 'src/context/canContext';
import { isAllSelection } from 'src/utils/selection';
import IconButton from 'switch-fe-shared/components/Buttons/IconButton';
import TextField from 'switch-fe-shared/components/TextInput/Textfield';

interface AvatarDropdownProps {
  options: any[];
  value: any;
  loading: boolean;
  label: string;
  getOptionLabel: (option: any) => string;
  onChange: (event: any, newValue: any) => void;
  isCollapsed: boolean;
  avatarColor?: string;
  enableCopyId?: boolean;
  ability?: any;
  openPath?: string | ((id: string) => string);
  canOpenAction?: string;
  canOpenSubject?: string;
  disabled?: boolean;
  disabledTooltip?: string;
}

interface StyledAutocompleteProps {
  options: any[];
  value: any;
  loading: boolean;
  label: string;
  getOptionLabel: (option: any) => string;
  onChange: (event: any, newValue: any) => void;
  sx?: AutocompleteProps<any, false, false, false>['sx'];
  enableCopyId?: boolean;
  ability?: any;
  openPath?: string | ((id: string) => string);
  canOpenAction?: string;
  canOpenSubject?: string;
  disabled?: boolean;
}

const StyledAvatar = styled(Avatar)(
  ({ theme }) => `
  width: 40px;
  height: 40px;
  background: ${theme.palette.primary.main};
  color: white;
  font-size: 16px;
  cursor: pointer;
  margin: 8px auto;

  &:hover {
    background: ${theme.palette.primary.dark};
    transform: scale(1.05);
  }

  transition: all 0.2s ease-in-out;
`
);

const sidebarSkeletonSx = {
  bgcolor: 'rgba(0, 0, 0, 0.12)',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  '&::after': {
    background:
      'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.45), transparent)',
  },
};

const AvatarDropdown = ({
  options,
  value,
  loading,
  label,
  getOptionLabel,
  onChange,
  isCollapsed,
  avatarColor,
  enableCopyId = false,
  ability,
  openPath,
  canOpenAction,
  canOpenSubject,
  disabled = false,
  disabledTooltip,
}: AvatarDropdownProps) => {
  /* Hooks & Use Effects */
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  /* States */
  const popoverOpen = Boolean(anchorEl);

  /* API Calls */

  /* Functions & Functional Components */
  const getAvatarLetter = (item: any) => {
    if (!item || !item.name) return '?';
    return item.name.charAt(0).toUpperCase();
  };

  const handleCopyId = () => {
    if (value?._id) {
      navigator.clipboard.writeText(value._id);
    }
  };

  const showCopyButton =
    enableCopyId && ability?.can(ACTIONS.MANAGE, 'all') && value?._id;

  const getOpenPath = (id: string) =>
    typeof openPath === 'function'
      ? openPath(id)
      : openPath?.replace(':id', id);

  const getTooltipText = () => {
    if (disabled) {
      return disabledTooltip || `Select ${label}`;
    }
    if (loading) return 'Loading...';
    if (!value || !value.name) return `Select ${label}`;
    return value.name;
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleSelectionChange = (event: any, newValue: any) => {
    onChange(event, newValue);
    handlePopoverClose();
  };

  React.useEffect(() => {
    if (disabled) {
      handlePopoverClose();
    }
  }, [disabled]);

  const StyledAutocomplete = ({
    options,
    value,
    loading,
    label,
    getOptionLabel,
    onChange,
    sx,
    enableCopyId,
    ability,
    openPath,
    canOpenAction,
    canOpenSubject,
    disabled,
  }: StyledAutocompleteProps) => {
    return (
      <Autocomplete
        options={options ?? []}
        disableClearable
        loading={loading}
        value={value || { _id: '', name: '' }}
        sx={sx}
        disabled={disabled}
        getOptionLabel={getOptionLabel}
        onChange={onChange}
        renderInput={(params) => {
          const showCopyInAutocomplete =
            enableCopyId &&
            ability?.can(ACTIONS.MANAGE, 'all') &&
            value?._id &&
            !isAllSelection(value._id);
          const showOpenInAutocomplete =
            openPath &&
            canOpenAction &&
            canOpenSubject &&
            value?._id &&
            !isAllSelection(value._id);

          return (
            <TextField
              {...params}
              label={label}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color='inherit' size={20} />
                    ) : null}
                    {showOpenInAutocomplete && (
                      <Can I={canOpenAction} a={canOpenSubject}>
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const path = getOpenPath(value._id);
                            if (path) {
                              navigate(path);
                            }
                          }}
                          title={`Open ${label}`}
                        >
                          <OpenInNew fontSize='small' />
                        </IconButton>
                      </Can>
                    )}
                    {showCopyInAutocomplete && (
                      <IconButton
                        size='small'
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigator.clipboard.writeText(value._id);
                        }}
                        title={`Copy ${label} ID`}
                      >
                        <ContentCopy fontSize='small' />
                      </IconButton>
                    )}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              InputLabelProps={{
                sx: {
                  color: '#303030',
                },
                classes: {
                  focused: 'focused',
                  shrink: 'shrink',
                },
              }}
              sx={{
                width: '100%',
                color: 'white',
                style: { fontSize: 15 },
                Input: {
                  color: '#303030',
                },
                '& fieldset': {
                  borderRadius: '20px',
                },
              }}
            />
          );
        }}
      />
    );
  };

  if (isCollapsed) {
    return (
      <ListItem>
        {loading ? (
          <Skeleton
            variant='circular'
            width={40}
            height={40}
            animation='wave'
            sx={{ ...sidebarSkeletonSx, mx: 'auto', my: 1 }}
          />
        ) : (
          <Tooltip title={getTooltipText()} placement='right'>
            <StyledAvatar
              onClick={handleAvatarClick}
              sx={{
                background: avatarColor || theme.palette.primary.main,
                opacity: disabled ? 0.6 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {getAvatarLetter(value)}
            </StyledAvatar>
          </Tooltip>
        )}
        <Popover
          open={popoverOpen}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          disableScrollLock
        >
          <Box sx={{ p: 2, minWidth: 280 }}>
            <StyledAutocomplete
              options={options}
              value={value}
              loading={loading}
              label={label}
              getOptionLabel={getOptionLabel}
              onChange={handleSelectionChange}
              sx={{ flex: 1 }}
              enableCopyId={enableCopyId}
              ability={ability}
              openPath={openPath}
              canOpenAction={canOpenAction}
              canOpenSubject={canOpenSubject}
              disabled={disabled}
            />
          </Box>
        </Popover>
      </ListItem>
    );
  }

  return (
    <ListItem sx={{ padding: 0 }}>
      {loading ? (
        <Skeleton
          variant='rounded'
          width='100%'
          height={48}
          animation='wave'
          sx={{
            ...sidebarSkeletonSx,
            flex: 1,
            mt: 2,
            mb: 1,
            mx: 2,
            borderRadius: 5,
          }}
        />
      ) : (
        <StyledAutocomplete
          options={options}
          value={value}
          loading={loading}
          label={label}
          getOptionLabel={getOptionLabel}
          onChange={onChange}
          sx={{ flex: 1, mt: 2, mb: 1, ml: 1, mr: 1, px: 1 }}
          enableCopyId={enableCopyId}
          ability={ability}
          openPath={openPath}
          canOpenAction={canOpenAction}
          canOpenSubject={canOpenSubject}
          disabled={disabled}
        />
      )}
    </ListItem>
  );
};

export default AvatarDropdown;
